import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import LeagueCard from "./LeagueCard";
import { bannerTheme } from "../utils/leagueBanner";
import { leagueStatus } from "../utils/leagueStatus";

// The league cards on /home, side by side. Native horizontal scrolling with
// snap points, so a swipe behaves the way the platform's own carousels do and
// no gesture code is needed. Each card takes the full width; with more than
// one league, an arrow on either side says there is another and steps to it,
// and dots below say which is in view. Arrows rather than a sliver of the next
// card, which read as clutter at the card's edge.
//
// Each card keeps its own height, and the rail takes the height of the one in
// view. Stretching every card to the tallest padded the shorter ones with empty
// space; holding the rail at the tallest left a pre-draft card above a block
// of blank page. The cost is that what sits below the rail moves when a swipe
// lands on a card of a different height, so the change is eased.

const GAP = 10;
// Reaching 8px into the card, clear of the table's rank numbers inside its
// 14px padding, and the rest into the 16px page gutter.
const ARROW = 24;
const ARROW_INSET = 8;

function LeagueRail({ leagues }) {
  const railRef = useRef(null);
  // active follows the swipe, for the dots and arrows. settled is the card
  // the swipe came to rest on, and only it sets the rail's height: resizing
  // the rail mid-drag moved everything below it during a sideways swipe, and
  // resizing a snapping scroll box makes Safari re-snap, which read as a jump
  // and could carry a swipe past the next card.
  const [active, setActive] = useState(0);
  const [settled, setSettled] = useState(0);
  const settleTimer = useRef(null);
  const many = leagues.length > 1;
  const [heights, setHeights] = useState([]);

  // Card heights change after the first paint (logos, a live refresh, a
  // fixture line opening), so they are watched rather than read once.
  useEffect(() => {
    const rail = railRef.current;
    if (!many || !rail || typeof ResizeObserver === "undefined") return undefined;
    const cards = [...rail.children];
    const measure = () => setHeights(cards.map((c) => c.offsetHeight));
    const observer = new ResizeObserver(measure);
    cards.forEach((c) => observer.observe(c));
    measure();
    return () => observer.disconnect();
  }, [many, leagues]);
  const railHeight = many && heights[settled] ? heights[settled] + 2 : undefined;

  // Built once per leagues list, so a swipe re-renders no card but the two
  // whose inView flips (LeagueCard is memoized).
  const cards = useMemo(() => leagues.map((league) => ({
    league,
    // The deep variant in both modes: the header stays dark on a dark page,
    // as the design has it, and the navy-black card behind it separates.
    theme: bannerTheme(league.id, "light"),
    status: leagueStatus({ league, draft: league.draft, standing: league.standing }),
  })), [leagues]);

  const onScroll = () => {
    const rail = railRef.current;
    if (!rail || !rail.firstElementChild) return;
    const step = rail.firstElementChild.getBoundingClientRect().width + GAP;
    const index = Math.min(leagues.length - 1, Math.round(rail.scrollLeft / step));
    setActive(index);
    // Settled once scrolling stops: scrollend where the browser has it, and a
    // short quiet spell everywhere, since older Safari does not fire it.
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => setSettled(index), 150);
  };
  useEffect(() => () => clearTimeout(settleTimer.current), []);

  const scrollTo = (i) => {
    const rail = railRef.current;
    const card = rail?.children[i];
    if (card) rail.scrollTo({ left: card.offsetLeft - rail.offsetLeft, behavior: "smooth" });
  };

  return (
    <div>
      <div style={{ position: "relative" }}>
        <div
          ref={railRef}
          onScroll={many ? onScroll : undefined}
          className="league-rail"
          style={{
            display: "flex", alignItems: "flex-start", gap: GAP, overflowX: many ? "auto" : "visible",
            overflowY: many ? "hidden" : "visible", height: railHeight, transition: "height 0.25s ease",
            scrollSnapType: many ? "x mandatory" : undefined, scrollbarWidth: "none",
            // Room for the cards' bottom border inside the scroll box.
            paddingBottom: 2,
          }}
        >
          {cards.map(({ league, theme, status }, i) => (
            <div
              key={league.id}
              // snapStop always: one card per swipe, however quick the flick.
              // Without it momentum carried a swipe past the next league.
              style={{ flex: "0 0 100%", minWidth: 0, scrollSnapAlign: "start", scrollSnapStop: "always" }}
            >
              <LeagueCard league={league} theme={theme} status={status} inView={i === settled} />
            </div>
          ))}
        </div>
        {/* On the card's edge, mostly in the page gutter, halfway down the
            card in view, the usual place for a carousel's arrows. The rail
            eases to each card's height, so they glide with it. Only the
            directions that lead somewhere are shown. */}
        {many && active > 0 && (
          <RailArrow side="left" label="Previous league" onClick={() => scrollTo(active - 1)} />
        )}
        {many && active < leagues.length - 1 && (
          <RailArrow side="right" label="Next league" onClick={() => scrollTo(active + 1)} />
        )}
      </div>
      <style>{`.league-rail::-webkit-scrollbar { display: none; }`}</style>

      {many && (
        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 10 }}>
          {leagues.map((league, i) => (
            <button
              key={league.id}
              type="button"
              aria-label={`Show ${league.name || "league"}`}
              aria-current={i === active ? "true" : undefined}
              onClick={() => scrollTo(i)}
              style={{
                width: i === active ? 16 : 6, height: 6, borderRadius: 3, border: "none", padding: 0, cursor: "pointer",
                backgroundColor: i === active ? "var(--color-brand)" : "var(--color-border)",
                transition: "width 0.2s",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RailArrow({ side, label, onClick }) {
  const Icon = side === "left" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{
        position: "absolute", top: "50%", [side]: ARROW_INSET - ARROW, transform: "translateY(-50%)", zIndex: 3,
        width: ARROW, height: ARROW, borderRadius: "50%", padding: 0, cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        backgroundColor: "var(--color-bg-card)", border: "1px solid var(--color-border)",
        boxShadow: "0 1px 4px rgba(0,0,0,0.15)", color: "var(--color-text-strong)",
      }}
    >
      <Icon size={14} strokeWidth={2.75} />
    </button>
  );
}

export default LeagueRail;
